import { useCallback, useContext, useMemo } from 'react';
import Promise from 'bluebird';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import {
  readCampaignNegativeWebpages,
  readDayTimeCriterions,
  readLocationCriterions,
  readNewCustomerAcquisitionGoals,
  // createOrUpdateAssetGroup,
} from '@bingads-webui-campaign-react/perf-max-data';
import {
  loadCampaign,
  modelToViewModelForCampaign,
  loadBundleCountries,
  loadAssetGroupWithVideos,
  saveAssetGroupAndLinkedData,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import cacheManager from '@bingads-webui-campaign-legacy/cache-manager/index';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { getODataConfig } from '../../shared/components/odata-config';

export const useAssetGroupSettingStepRepository = () => {
  const { state } = useWizardService();
  const { scope } = useContext(ScopeContext) || {};

  const serverContext = useContext(ServerContext);
  const activity = useContext(ActivityContext);
  const permissions = useContext(PermissionsContext);
  const scenarioContext = useContext(ScenarioContext);
  const isSearchThemeEnabled = serverContext.DynamicPermissions.SearchThemes;
  const isUrlRulesEnabled = serverContext.DynamicPermissions.AssetGroupUrlRules;
  const isNewCustomerAcquisitionEnabled = serverContext.DynamicPermissions.NewCustomerAcquisition;

  const i18n = useContext(I18nContext);

  const {
    cid, aid, campaignId, assetGroupId,
  } = scope.values;

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const {
    PMaxNewAspectRatios: isPMaxNewAspectRatiosEnabled,
    PMaxExcludeNewAspectRatios: isPMaxNewAspectRatiosExcluded,
  } = serverContext.DynamicPermissions || {};

  const isPMaxNewAspectRatios = isPMaxNewAspectRatiosEnabled && !isPMaxNewAspectRatiosExcluded;

  const versionForLog = getLogVersion(permissions);

  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'PMaxAdsStep');

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });

  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const {
    isPublishing,
  } = state.context.wizardData;

  const uploadVideoIfNeeded = useCallback(({ assetGroup }) => {
    const videoPromises = [];

    if (!assetGroup.videos) {
      return Promise.resolve(assetGroup);
    }

    // check for videos in the assetGroup that dont have an id.
    assetGroup.videos.forEach((video) => {
      if (!video.id) {
        videoPromises.push(uploadVideoMetadata({
          videoMetadata: video,
          newVideoFromMTObject,
          folderRepository,
          imageRepository,
          videoRepository,
        }).then((newVideo) => {
          _.extend(video, newVideo);
        }));
      }
    });

    if (_.isEmpty(videoPromises)) {
      return Promise.resolve(assetGroup);
    }

    return Promise.all(videoPromises).then(() => assetGroup);
  }, [folderRepository, imageRepository, videoRepository]);

  const stepName = useMemo(() => (assetGroupId ? 'settings' : 'creation'), [assetGroupId]);

  const load = useCallback(() => {
    activity.trace(`[PmaxNorthStarAssetGroup${stepName}Wizard] - loadData started in asset group ${stepName} step`);
    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
      campaignNegativeWebpages: readCampaignNegativeWebpages(cid, aid, campaignId),
      assetGroup: assetGroupId ? loadAssetGroupWithVideos(cid, aid, campaignId, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled, videoRepository) : {},
      ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoals: readNewCustomerAcquisitionGoals(cid, aid) }),
    };

    if (isPMaxNewAspectRatios) {
      activity.trace('[PmaxNorthStarAssetGroupSettingWizard] - New Aspect Ratios is enabled');
    }

    return Promise.props(loadPromises).then(({
      campaign,
      locationTargets,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
      campaignNegativeWebpages,
      assetGroup,
      newCustomerAcquisitionGoals,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        campaignNegativeWebpagesModel: campaignNegativeWebpages ? campaignNegativeWebpages.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoalsModel: newCustomerAcquisitionGoals ? newCustomerAcquisitionGoals.value : [] }),
      });

      const data = {
        campaign: campaignData.data,
        assetGroup: assetGroup.data,
      };
      activity.trace(`[PmaxNorthStarAssetGroup${stepName}Wizard] - loadData succeeded in asset group ${stepName} step`);
      activity.signal(`[PmaxNorthStarAssetGroup${stepName}Wizard] - loadData succeeded in asset group ${stepName} step`);

      return data;
    }).catch((errorData) => {
      activity.trace(`[PmaxNorthStarAssetGroup${stepName}Wizard] loadData failed in asset group ${stepName} step. Error:  ${JSON.stringify(errorData)}`);

      if (assetGroupId) {
        setClarityTag({ IsPmaxNorthStarAssetGroupSettingFailure: 'true' });
      } else {
        setClarityTag({ IsPmaxNorthStarAssetGroupCreationFailure: 'true' });
      }

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, stepName, cid, aid, campaignId, serverContext, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled, videoRepository, isNewCustomerAcquisitionEnabled, isPMaxNewAspectRatios]);

  const save = useCallback(({ data, initialData }) => {
    if (isPMaxNewAspectRatios) {
      activity.trace('[PmaxNorthStarAssetGroupSettingWizard] - New Aspect Ratios is enabled');
    }

    if (isPublishing) {
      return uploadVideoIfNeeded({ assetGroup: data.assetGroup }).then(() => saveAssetGroupAndLinkedData({
        assetGroup: { data: data.assetGroup },
        originalAssetGroup: { data: initialData.assetGroup },
        campaign: data.campaign,
      }, {
        scenario: scenarioContext.name,
        i18n,
        activityLogger: scenarioContext.createActivity('updateAssetGroup'),
        serverContext,
        cid,
        aid,
        activity,
      }).then(() => {
        activity.trace('[PmaxNorthStarAssetGroupSettingWizard] - asset group settings updated');
        activity.signal('[PmaxNorthStarAssetGroupSettingWizard] - asset group settings updated');

        cacheManager.invalidate('AssetGroup');
      }).catch((errorData) => {
        activity.trace('[PmaxNorthStarAssetGroupSettingWizard] - asset group settings update failed');
        setClarityTag({ IsPmaxNorthStarAssetGroupSettingFailure: 'true' });
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: { ...errorData } });
      }));
    }

    // no update necessary if not publishing.
    return Promise.resolve({
      assetGroup: data.assetGroup,
    });
  }, [activity, aid, cid, i18n, isPMaxNewAspectRatios, isPublishing, scenarioContext, serverContext, uploadVideoIfNeeded]);

  return {
    load,
    save,
  };
};
